import React, { useState } from "react";
import PropTypes from "prop-types";
import {
	Table,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	CircularProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import TableStyle from "styles/application/TableStyle";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import useInfiniteScroll from "hooks/useInfiniteScroll";

const AT = TableStyle();

const useStyles = makeStyles()((theme) => ({
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
	},
	nameRow: {
		width: "20%",
	},
	nonSapAssetNoColumn: {
		width: "40%",
	},
	nonSapDescriptionColumn: {
		width: "50%",
	},
	actionColumn: {
		width: "10%",
		minWidth: "100px",
	},
	sapModeAssetCell: {
		fontWeight: "500",
		color: ColourConstants.commonText,
		fontFamily: "Roboto Condensed",
	},
	referenceRow: {
		backgroundColor: "#fafafa",
	},
}));

const AssetRegisterTable = ({
    data,
    tableConfig,
    onEdit,
    onDelete,
    page,
    onPageChange,
    count,
    isLoading,
    searchText,
    isReadOnly = false,
    currentTableSort,
    handleSort,
    isSAPMode,
    customCaptions,
}) => {
	const { classes, cx } = useStyles();
	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

    const { loading } = useInfiniteScroll(
        data,
        count,
        async (pageNo, prevData, sortOptions, searchText) =>
            await onPageChange(pageNo + 1, prevData, sortOptions),
        page,
        searchText,
        window,
        {
            sortField: currentTableSort[0],
            sortOrder: currentTableSort[1],
        }
    );

    const handleSortClick = (field) => {
        const newMethod =
            field === currentTableSort[0] && currentTableSort[1] === "asc"
                ? "desc"
                : "asc";

        handleSort(field, newMethod);
    };

	const handlePopup = (event, index) => {
		setAnchorEl(anchorEl === event.currentTarget ? null : event.currentTarget);
		setSelectedData(anchorEl === event.currentTarget ? null : index);
	};

	const handleClosePopup = () => {
		setAnchorEl(null);
		setSelectedData(null);
	};

	const getCellValue = (row, column) => {
		const displayData = row.displayData || row;
		return displayData[column];
	};

	const getRowClassName = (row) => {
		if (isSAPMode && row.isAssetReference) {
			return classes.referenceRow;
		}
		return "";
	};

	const getColumnClassName = (column, index) => {
		// In non-SAP mode, apply specific widths
		if (!isSAPMode) {
			if (column === "assetNo") {
				return classes.nonSapAssetNoColumn;
			}
			if (column === "description") {
				return classes.nonSapDescriptionColumn;
			}
		}
		
		// Default SAP mode or other columns use nameRow
		return classes.nameRow;
	};

	const getHeaderClassName = (column, index) => {
		const baseClasses = cx(getColumnClassName(column, index), {
			[classes.selectedTableHeadRow]:
				currentTableSort[0] === tableConfig.columns[index],
			[classes.tableHeadRow]:
				currentTableSort[0] !== tableConfig.columns[index],
		});
		
		return baseClasses;
	};

	if (isLoading) {
		return <CircularProgress />;
	}

	const getMenuData = (row) => {
		// Use appropriate ID based on mode - referenceId for SAP mode, id for regular mode
		const deleteId = isSAPMode && row.isAssetReference ? row.referenceId || row.id : row.id;
		
		const baseMenu = [
			{
				name: "Edit",
				handler: () => onEdit(row.id),
				isDelete: false,
			},
		];

		if (!isReadOnly) {
			baseMenu.push({
				name: "Delete",
				handler: () => onDelete(deleteId),
				isDelete: true,
			});
		}

		return baseMenu;
	};

	return (
		<>
		<AT.TableContainer component={Paper} elevation={0}>
				<Table aria-label="Asset Register Table">
					<AT.TableHead>
						<TableRow>
							{tableConfig.headers.map((header, index) => (
								<TableCell
									key={header}
									onClick={() => {
										handleSortClick(tableConfig.columns[index]);
									}}
									className={getHeaderClassName(tableConfig.columns[index], index)}
								>
									<AT.CellContainer className="flex justify-between">
										<div style={{ width: "150px" }}>{header}</div>
										<div className="arrow">
											<AT.DescArrow
												fill={
													currentTableSort[0] === tableConfig.columns[index] &&
													currentTableSort[1] === "asc"
														? "#D2D2D9"
														: "#F9F9FC"
												}
												className="arrowUp"
											/>
											<AT.DefaultArrow
												fill={
													currentTableSort[0] === tableConfig.columns[index] &&
													currentTableSort[1] === "desc"
														? "#D2D2D9"
														: "#F9F9FC"
												}
												className="arrowDown"
											/>
										</div>
									</AT.CellContainer>
								</TableCell>
							))}
							{!isReadOnly && (
								<TableCell className={cx(classes.tableHeadRow, classes.actionColumn)}>
									<AT.CellContainer>{customCaptions?.action || "Action"}</AT.CellContainer>
								</TableCell>
							)}
						</TableRow>
					</AT.TableHead>
					<TableBody>
						{data.map((row, index) => (
							<TableRow 
								key={row.id}
								className={getRowClassName(row)}
								hover
							>
								{tableConfig.columns.map((column, colIndex) => (
									<AT.DataCell 
										key={`${row.id}-${column}`}
										className={cx(
											getColumnClassName(column, colIndex),
											{
												[classes.sapModeAssetCell]: 
													isSAPMode && 
													column === "assetNo" && 
													row.isAssetReference
											}
										)}
									>
										<AT.TableBodyText>{getCellValue(row, column)}</AT.TableBodyText>
									</AT.DataCell>
								))}
								{!isReadOnly && (
									<AT.DataCell className={classes.actionColumn}>
										<AT.CellContainer>
											<AT.DotMenu onClick={(e) => handlePopup(e, index)}>
												<AT.TableMenuButton>
													<MenuIcon />
												</AT.TableMenuButton>
												<PopupMenu
													index={index}
													selectedData={selectedData}
													anchorEl={anchorEl}
													id={row.id}
													clickAwayHandler={handleClosePopup}
													menuData={getMenuData(row)}
												/>
											</AT.DotMenu>
										</AT.CellContainer>
									</AT.DataCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
				{loading && (
					<div style={{ textAlign: "center", padding: "20px" }}>
						<CircularProgress />
					</div>
				)}
			</AT.TableContainer>
		</>
	);
};

AssetRegisterTable.propTypes = {
    data: PropTypes.array.isRequired,
    tableConfig: PropTypes.shape({
        headers: PropTypes.array.isRequired,
        columns: PropTypes.array.isRequired,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    count: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    searchText: PropTypes.string,
    isReadOnly: PropTypes.bool,
    currentTableSort: PropTypes.array.isRequired,
    handleSort: PropTypes.func.isRequired,
    isSAPMode: PropTypes.bool.isRequired,
    customCaptions: PropTypes.object,
};

export default React.memo(AssetRegisterTable);
